import { memo, useMemo } from "react";
import { TrashIcon } from "@heroicons/react/outline";

import ConditionValueInput from "./ConditionValueInput";
import { isUnaryOperator, getRightInputPlaceholder } from "./constants";
import JsonPath from "./JsonPath";
import OperatorSelect from "./OperatorSelect";

import { ConditionsProps } from ".";

import { EdgeCondition } from "@/components/Workflows/types";

const SingleCondition = memo(
  ({
    condition,
    onUpdateCondition,
    onRemoveCondition,
    readonly,
  }: {
    condition: EdgeCondition;
    onUpdateCondition: ConditionsProps["onUpdateCondition"];
    onRemoveCondition: (id: string) => void;
    readonly?: boolean;
  }) => {
    const isUnary = useMemo(() => isUnaryOperator(condition.operator), [condition.operator]);
    const placeholder = useMemo(
      () => getRightInputPlaceholder(condition.operator),
      [condition.operator],
    );

    return (
      <div className="flex w-full flex-row gap-x-2" key={condition.id}>
        <div className="mb-2 w-full rounded-lg border border-gray-100 bg-white p-3 shadow-sm transition-colors duration-200 hover:border-gray-200">
          <div className="flex items-start space-x-3">
            <div className="flex-1">
              <ConditionValueInput
                className="text-sm focus:ring-2 focus:ring-blue-100"
                value={condition.left_config}
                onChange={value => onUpdateCondition(condition.position, "left_config", value)}
                operator={condition.operator}
                readonly={readonly}
                isLeftSide
              />
            </div>

            <div className="flex-shrink-0">
              {!readonly && <div className="h-9" />}
              <OperatorSelect
                value={condition.operator}
                onChange={value => onUpdateCondition(condition.position, "operator", value)}
                readonly={readonly}
              />
            </div>

            {!isUnary && (
              <div className="flex-1">
                <ConditionValueInput
                  className="text-sm focus:ring-2 focus:ring-blue-100"
                  value={condition.right_config}
                  onChange={value => onUpdateCondition(condition.position, "right_config", value)}
                  placeholder={placeholder}
                  operator={condition.operator}
                  readonly={readonly}
                />
              </div>
            )}
          </div>
          <div className="flex flex-row justify-between">
            <div className="flex-1">
              {"source" in condition.left_config && typeof condition.left_config === "object" ? (
                <JsonPath />
              ) : null}
            </div>
            <div className="w-[180px]" />
            <div className="flex-1">
              {!isUnary &&
              "source" in condition.right_config &&
              typeof condition.right_config === "object" ? (
                <JsonPath />
              ) : null}
            </div>
          </div>
        </div>
        {!readonly && (
          <div className="flex flex-col items-center justify-center">
            <button
              onClick={() => onRemoveCondition(condition.id)}
              className="rounded-full p-2 text-gray-400 transition-colors duration-200 hover:bg-red-50 hover:text-red-600"
              title="Remove condition"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    );
  },
);

export default SingleCondition;
